<template>
  <div class="component-demographic">
    <div class="demographic-top">
      <h1 class="title">Record of Participation</h1>
      <p class="desc">Please fill in the participation form</p>
    </div>
    <p>
      <label for="language">Language: </label>
      <FormSelector class="form-selector-box" placeholder="Language" :data="languageList" v-model.number="languageId" />
    </p>
    <p>
      <label for="province">Province: </label>
      <FormSelector class="form-selector-box" placeholder="Province" :data="provinceList" v-model.number="provinceId" />
    </p>

    <div class="demographics">
      <DemographicsList :data="demoList" @update="updateDemoList" />
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, watch } from 'vue';

import FormSelector from '@/components/form/elements/FormSelector.vue';
import DemographicsList from '@/components/participation/DemographicsList.vue';
import MetaService from '@/services/meta';
import ProvinceService from '@/services/province';

const emit = defineEmits(['updateDemographicsInfo']);
const languageId = ref(0);
const provinceId = ref(0);

const demoList = ref([
  {
    id: 0, // used for list animation, not send to API
    ageId: 0,
    genderId: 0,
    numParticipants: null, // allows placeholder to show in counter, but is a Number value
    error: false,
  },
  {
    id: 1,
    ageId: 0,
    genderId: 0,
    numParticipants: null,
    error: false,
  },
]);

const nextId = ref(2);

const languageList = ref([]);
const provinceList = ref([]);

onBeforeMount(async () => {
  const languageResutls = await MetaService.getMetasByType('language');
  languageList.value = languageResutls.records.map((l) => ({ value: l.id, name: l.meta_value }));

  const provinceData = await ProvinceService.getProvinces();
  provinceList.value = provinceData.records.map((p) => ({ value: p.id, name: p.name }));
});

function updateDemographicsInfo() {
  emit('updateDemographicsInfo', {
    languageId: languageId.value,
    provinceId: provinceId.value,
    demoList: demoList.value,
  });
}

function updateDemoList(newDemoList) {
  let replacementList = newDemoList; // will be modified before inserted into data

  // functional paradigms will be abstained in favor of performance for the error searches

  // set error: true for all duplicate age/genders
  // using map for O(n) complexity
  const hashMap = new Map();
  replacementList.forEach((item, index) => {
    const newDemoItem = item;
    newDemoItem.error = false; // innocent until proven guilty
    if (newDemoItem.ageId !== 0 && newDemoItem.genderId !== 0) {
      const hash = `${newDemoItem.ageId},${newDemoItem.genderId}`; // only one value per age/gender
      if (hashMap.has(hash)) {
        newDemoItem.error = true;
        replacementList[hashMap.get(hash)].error = true;
      } else {
        hashMap.set(hash, index);
      }
    }
  });

  // if every demo item has at least one dropdown selected, create new empty demo item
  // console.log('replacement list');
  // console.log(replacementList);
  if (replacementList.every((demoItem) => demoItem.ageId !== 0 || demoItem.genderId !== 0)) {
    replacementList = [
      ...replacementList,
      {
        id: nextId.value,
        ageId: 0,
        genderId: 0,
        numParticipants: null,
        error: false,
      },
    ];
    nextId.value += 1;
  }

  demoList.value = replacementList;

  updateDemographicsInfo();
}

watch(languageId, () => {
  updateDemographicsInfo();
});

watch(provinceId, () => {
  updateDemographicsInfo();
});
</script>

<style scoped>
.component-demographic {
  display: flex;
  flex-direction: column;

  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 0 1em;
}

.demographic-top .title {
  margin: 1em 0 0;
  padding: 0;
}
.demographic-top .desc {
  font-size: 1.1em;
  color: #6d6e71;
}

.component-demographic > p {
  margin: 0.5em 0;
  max-width: 25em;
  padding: 0;
}

label {
  color: #6d6e71;
  font-size: 1.1em;
}
.form-selector-box {
  margin-top: 0.5em;
}

.demographics {
  margin-top: 1em;
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  .component-demographic {
    padding: 2em 4em;
  }
}
</style>
